import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, Clock, CheckCircle, MessageCircle, Send, 
  FileText, DollarSign, AlertCircle, Loader, User, Image, Paperclip
} from 'lucide-react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { EnhancedChatInput } from '../components/EnhancedChatInput';
import { api, useAuth } from '../lib/auth';
import { formatDateTime, formatCurrency, getStatusColor, getStatusLabel, cn } from '../lib/utils';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_TIMELINE = [
  { status: 'submitted', label: 'Submitted', icon: FileText },
  { status: 'accepted', label: 'Accepted', icon: CheckCircle },
  { status: 'pending', label: 'Pending', icon: Clock },
  { status: 'in_progress', label: 'In Progress', icon: Loader },
  { status: 'needs_client_approval', label: 'Needs Approval', icon: AlertCircle },
  { status: 'completed', label: 'Completed', icon: CheckCircle },
];

export default function OrderDetailPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [messages, setMessages] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadData();
  }, [orderId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadData = async () => {
    try {
      const [orderRes, messagesRes, proposalsRes] = await Promise.all([
        api.getOrder(orderId),
        api.getOrderMessages(orderId),
        api.getProposals()
      ]);
      setOrder(orderRes.data);
      setMessages(messagesRes.data);
      setProposals(proposalsRes.data.filter(p => p.order_id === orderId));
    } catch (error) {
      console.error('Failed to load order:', error);
      toast.error('Order not found');
      navigate('/orders');
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (messageData) => {
    const body = typeof messageData === 'string' ? messageData : messageData.content;
    const attachments = messageData.attachments || null;
    
    if (!body?.trim() && !attachments?.length) return;
    
    setSendingMessage(true);
    try {
      await api.addOrderMessage(orderId, { 
        body: body,
        attachments: attachments,
        mentions: messageData.mentions || null
      });
      setNewMessage('');
      loadData();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleAIWriter = async (text) => {
    // AI Writer to enhance/improve the message
    try {
      const response = await fetch(`${API_URL}/api/ai/enhance-message`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ message: text, context: `Order conversation for: ${order?.service_name}` })
      });
      
      if (response.ok) {
        const data = await response.json();
        return data.enhanced_message;
      }
    } catch (error) {
      console.error('AI Writer error:', error);
    }
    return null;
  };

  const handleApproveProposal = async (proposalId) => {
    try {
      const response = await api.approveProposal(proposalId);
      toast.success('Proposal approved!');
      
      if (response.data.requires_payment) {
        const proposal = proposals.find(p => p.id === proposalId);
        if (proposal && proposal.due_amount > 0) {
          const checkoutRes = await api.createCheckout(proposalId);
          window.location.href = checkoutRes.data.url;
        }
      }
      
      loadData();
    } catch (error) {
      toast.error('Failed to approve proposal');
    }
  };

  const currentStatusIndex = STATUS_TIMELINE.findIndex(s => s.status === order?.status);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="max-w-5xl mx-auto px-4 py-8">
          <div className="h-96 bg-white/5 rounded-2xl animate-pulse" />
        </div>
      </DashboardLayout>
    );
  }

  if (!order) {
    return (
      <DashboardLayout>
        <div className="max-w-5xl mx-auto px-4 py-8 text-center">
          <p className="text-gray-400">Order not found</p>
          <Link to="/orders">
            <Button className="btn-secondary mt-4">Back to Orders</Button>
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  const activeProposal = proposals.find(p => ['sent', 'approved'].includes(p.status));

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Back & Header */}
        <Link 
          to="/orders" 
          className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Orders
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col lg:flex-row gap-4 items-start justify-between mb-8"
        >
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className={cn(
                "px-3 py-1 rounded-full text-sm font-medium border",
                getStatusColor(order.status)
              )}>
                {getStatusLabel(order.status)}
              </span>
            </div>
            <h1 className="font-heading text-2xl font-bold text-white mb-1">
              {order.service_title || 'Service Order'}
            </h1>
            <p className="text-gray-400">{order.department_name}</p>
          </div>
        </motion.div>

        {/* Status Timeline */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="glass rounded-xl p-6 mb-8"
        >
          <h2 className="font-semibold text-white mb-4">Order Progress</h2>
          <div className="flex items-center justify-between overflow-x-auto pb-2">
            {STATUS_TIMELINE.slice(0, -1).map((step, i) => {
              const isActive = i <= currentStatusIndex;
              const isCurrent = step.status === order.status;
              const Icon = step.icon;
              
              return (
                <React.Fragment key={step.status}>
                  <div className="flex flex-col items-center min-w-[80px]">
                    <div className={cn(
                      "w-10 h-10 rounded-full flex items-center justify-center mb-2 transition-colors",
                      isCurrent ? "bg-primary text-black" :
                      isActive ? "bg-primary/20 text-primary" :
                      "bg-white/5 text-gray-600"
                    )}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <span className={cn(
                      "text-xs text-center",
                      isCurrent ? "text-primary font-medium" :
                      isActive ? "text-gray-300" :
                      "text-gray-600"
                    )}>
                      {step.label}
                    </span>
                  </div>
                  {i < STATUS_TIMELINE.length - 2 && (
                    <div className={cn(
                      "flex-1 h-0.5 mx-2",
                      i < currentStatusIndex ? "bg-primary" : "bg-white/10"
                    )} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Proposal Card (if exists) */}
            {activeProposal && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="glass rounded-xl p-6 border-2 border-primary/30"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Price Proposal</h3>
                    <p className="text-sm text-gray-400">
                      {activeProposal.status === 'sent' ? 'Awaiting your approval' : 'Approved'}
                    </p>
                  </div>
                </div>

                <div className="space-y-3 mb-6">
                  {activeProposal.line_items_json?.map((item, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span className="text-gray-300">{item.description}</span>
                      <span className="text-white">{formatCurrency(item.amount)}</span>
                    </div>
                  ))}
                  <div className="border-t border-white/10 pt-3 flex justify-between">
                    <span className="text-gray-400">Total</span>
                    <span className="text-white font-semibold">{formatCurrency(activeProposal.total)}</span>
                  </div>
                  {activeProposal.covered_amount > 0 && (
                    <div className="flex justify-between text-green-400">
                      <span>Co-Op Coverage</span>
                      <span>-{formatCurrency(activeProposal.covered_amount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-lg font-bold">
                    <span className="text-white">Amount Due</span>
                    <span className="text-primary">{formatCurrency(activeProposal.due_amount)}</span>
                  </div>
                </div>

                {activeProposal.status === 'sent' && (
                  <Button 
                    onClick={() => handleApproveProposal(activeProposal.id)}
                    className="btn-primary w-full"
                    data-testid="approve-proposal-btn"
                  >
                    {activeProposal.due_amount > 0 ? 'Approve & Pay' : 'Approve to Start'}
                  </Button>
                )}
                
                {activeProposal.status === 'approved' && activeProposal.due_amount > 0 && (
                  <Button 
                    onClick={async () => {
                      try {
                        const checkoutRes = await api.createCheckout(activeProposal.id);
                        window.location.href = checkoutRes.data.url;
                      } catch (error) {
                        toast.error('Failed to create payment link');
                      }
                    }}
                    className="btn-primary w-full"
                    data-testid="pay-now-btn"
                  >
                    💳 Pay Now - {formatCurrency(activeProposal.due_amount)}
                  </Button>
                )}
                
                {activeProposal.due_amount > 0 && activeProposal.covered_amount === 0 && (
                  <div className="mt-4 p-3 rounded-lg bg-accent/10 border border-accent/30">
                    <p className="text-sm text-gray-300">
                      💡 <strong className="text-accent">Want 100% Co-Op coverage?</strong>{' '}
                      <a 
                        href="https://aihorsepowered.com" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        Get Funded →
                      </a>
                    </p>
                  </div>
                )}
              </motion.div>
            )}

            {/* Messages */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="glass rounded-xl p-6"
            >
              <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                <MessageCircle className="w-5 h-5" />
                Conversation
              </h3>

              <div className="space-y-4 max-h-96 overflow-y-auto mb-4">
                {messages.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No messages yet</p>
                ) : (
                  messages.map((msg) => {
                    const isProposal = msg.body?.includes('**Price Proposal**');
                    const isApproval = msg.body?.includes('**Proposal Approved!**');
                    
                    // Parse markdown-like formatting
                    const formatBody = (body) => {
                      return body
                        .replace(/\*\*(.*?)\*\*/g, '<strong class="text-white">$1</strong>')
                        .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank" class="text-primary hover:underline">$1</a>')
                        .replace(/\n/g, '<br />');
                    };
                    
                    return (
                      <div
                        key={msg.id}
                        className={cn(
                          "flex gap-3",
                          msg.sender_type === 'client' ? "justify-end" : ""
                        )}
                      >
                        {msg.sender_type !== 'client' && (
                          <div className={cn(
                            "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0",
                            msg.sender_type === 'ai' ? "bg-primary/20" : "bg-purple-500/20"
                          )}>
                            {msg.sender_type === 'ai' ? (
                              <MessageCircle className="w-4 h-4 text-primary" />
                            ) : (
                              <User className="w-4 h-4 text-purple-400" />
                            )}
                          </div>
                        )}
                        <div className={cn(
                          "max-w-[80%] rounded-2xl px-4 py-3",
                          msg.sender_type === 'client' 
                            ? "bg-primary text-black" 
                            : isProposal ? "bg-accent/10 border border-accent/30" : "bg-white/5"
                        )}>
                          <p className="text-xs text-gray-400 mb-1">
                            {msg.sender_name || (msg.sender_type === 'ai' ? 'AI Concierge' : 'Team')}
                            {' • '}
                            {formatDateTime(msg.created_at)}
                          </p>
                          <div 
                            className={msg.sender_type === 'client' ? "text-black" : "text-gray-200"}
                            dangerouslySetInnerHTML={{ __html: formatBody(msg.body) }}
                          />
                          
                          {/* Show Approve button for proposals */}
                          {isProposal && activeProposal?.status === 'sent' && user?.role === 'client' && (
                            <Button
                              onClick={() => handleApproveProposal(activeProposal.id)}
                              className="btn-primary mt-3 w-full"
                              data-testid="approve-in-thread-btn"
                            >
                              ✓ Approve {activeProposal.due_amount > 0 ? `& Pay $${activeProposal.due_amount.toFixed(2)}` : 'to Start'}
                            </Button>
                          )}
                          
                          {/* Show Pay Now button for approved proposals with payment due */}
                          {isApproval && activeProposal?.status === 'approved' && activeProposal?.due_amount > 0 && user?.role === 'client' && (
                            <Button
                              onClick={async () => {
                                try {
                                  const checkoutRes = await api.createCheckout(activeProposal.id);
                                  window.location.href = checkoutRes.data.url;
                                } catch (error) {
                                  toast.error('Failed to create payment link');
                                }
                              }}
                              className="btn-primary mt-3 w-full"
                              data-testid="pay-now-thread-btn"
                            >
                              💳 Pay Now - ${activeProposal.due_amount.toFixed(2)}
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              <EnhancedChatInput
                onSend={sendMessage}
                placeholder="Type a message... (supports emoji, files, @mentions)"
                disabled={sendingMessage}
                loading={sendingMessage}
                showAIWriter={true}
                onAIWrite={handleAIWriter}
                buttonColor="from-primary to-cyan-500"
              />
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Order Details */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="glass rounded-xl p-6"
            >
              <h3 className="font-semibold text-white mb-4">Order Details</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Created</span>
                  <span className="text-white">{formatDateTime(order.created_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Last Updated</span>
                  <span className="text-white">{formatDateTime(order.updated_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Order ID</span>
                  <span className="text-white font-mono text-xs">{order.id.slice(0, 8)}...</span>
                </div>
              </div>
            </motion.div>

            {/* Mission Brief */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="glass rounded-xl p-6"
            >
              <h3 className="font-semibold text-white mb-4">Mission Brief</h3>
              {order.structured_data_json && Object.keys(order.structured_data_json).length > 0 ? (
                <div className="space-y-3">
                  {Object.entries(order.structured_data_json).map(([key, value]) => (
                    <div key={key}>
                      <p className="text-xs text-gray-400 capitalize">{key.replace(/_/g, ' ')}</p>
                      <p className="text-sm text-white">{value || '-'}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400">{order.summary || 'No details provided'}</p>
              )}
              
              {/* Files/Attachments Section */}
              {order.files && order.files.length > 0 && (
                <div className="mt-6 pt-4 border-t border-white/10">
                  <h4 className="font-medium text-white mb-3 flex items-center gap-2">
                    <Paperclip className="w-4 h-4 text-primary" />
                    Uploaded Files ({order.files.length})
                  </h4>
                  <div className="space-y-2">
                    {order.files.map((fileUrl, idx) => {
                      const fileName = fileUrl.split('/').pop();
                      return (
                        <a 
                          key={idx}
                          href={fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors group"
                        >
                          <FileText className="w-4 h-4 text-gray-400 group-hover:text-primary" />
                          <span className="text-sm text-gray-300 group-hover:text-white truncate flex-1">
                            {fileName}
                          </span>
                          <span className="text-xs text-primary">View →</span>
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
